import React, { useEffect, useMemo, useState, useCallback, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Folder,
  FolderOpen,
  FolderPlus,
  Database,
  Search,
  X,
  ChevronRight,
  Plus,
} from 'lucide-react';
import { Modal } from './ui/Modal';
import { getOrderedChildren, getFolderById } from '../utils/collectionFolders';

const ROW_INDENT = 16; // px per depth level

/** Tiny inline name input (Tailwind-free) — commits on Enter/blur, cancels on Escape. */
function InlineCreateInput({ initialValue, onSave, onCancel }) {
  const [draft, setDraft] = useState(initialValue || '');
  const inputRef = useRef(null);

  useLayoutEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      onCancel();
      return;
    }
    onSave(trimmed);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      onBlur={commit}
      className="cpicker-inline-input modal-input-no-focus-outline"
    />
  );
}

/** Build the set of folder ids that should be visible for a search query. */
function computeVisibleFolderIds(collection, query) {
  const folders = collection?.settings?.folders || [];
  if (!query) return null; // null = no filtering
  const lower = query.toLowerCase();

  const childrenByParent = new Map();
  folders.forEach((f) => {
    const pid = f.parent_id == null ? null : String(f.parent_id);
    if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
    childrenByParent.get(pid).push(f);
  });
  const byId = new Map(folders.map((f) => [String(f.id), f]));

  const visible = new Set();
  const addSubtree = (folderId) => {
    visible.add(String(folderId));
    (childrenByParent.get(String(folderId)) || []).forEach((c) => addSubtree(c.id));
  };
  const addAncestors = (folder) => {
    let current = folder;
    while (current && current.parent_id != null) {
      const parent = byId.get(String(current.parent_id));
      if (!parent || visible.has(String(parent.id))) break;
      visible.add(String(parent.id));
      current = parent;
    }
  };

  folders
    .filter((f) => (f.name || '').toLowerCase().includes(lower))
    .forEach((f) => {
      addSubtree(f.id);
      addAncestors(f);
    });

  return visible;
}

const CollectionSelectionModal = ({
  isOpen,
  onClose,
  collections,
  onSelectCollection,
  title,
  description,
  allowFolders = false,
  allowCreate = false,
  showNameInput = false,
  initialName = '',
  confirmLabel,
  onCreateCollection,
  onCreateFolder,
}) => {
  const { t } = useTranslation();

  const [name, setName] = useState(initialName);
  const [search, setSearch] = useState('');
  const [expandedCollections, setExpandedCollections] = useState(() => new Set());
  const [expandedFolders, setExpandedFolders] = useState(() => new Set());
  const [selected, setSelected] = useState(null); // { collectionId, folderId|null }
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [creatingFolderFor, setCreatingFolderFor] = useState(null); // { collectionId, parentFolderId|null }
  const [busy, setBusy] = useState(false);

  // Reset transient state whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setName(initialName || '');
    setSearch('');
    setExpandedCollections(new Set());
    setExpandedFolders(new Set());
    setSelected(null);
    setCreatingCollection(false);
    setCreatingFolderFor(null);
    setBusy(false);
  }, [isOpen, initialName]);

  const query = search.trim();
  const searching = query.length > 0;

  const toggleCollection = useCallback((id) => {
    setExpandedCollections((prev) => {
      const next = new Set(prev);
      if (next.has(String(id))) next.delete(String(id));
      else next.add(String(id));
      return next;
    });
  }, []);

  const toggleFolder = useCallback((id) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(String(id))) next.delete(String(id));
      else next.add(String(id));
      return next;
    });
  }, []);

  const isCollectionExpanded = (id) => searching || expandedCollections.has(String(id));
  const isFolderExpanded = (id) => searching || expandedFolders.has(String(id));

  // Visible collections (filter by name or matching descendant folders).
  const visibleData = useMemo(() => {
    return (collections || []).map((collection) => {
      const visibleFolderIds = computeVisibleFolderIds(collection, query);
      const nameMatches = !searching || (collection.name || '').toLowerCase().includes(query.toLowerCase());
      const hasVisibleFolder = visibleFolderIds && visibleFolderIds.size > 0;
      const visible = !searching || nameMatches || hasVisibleFolder;
      return { collection, visibleFolderIds, visible };
    });
  }, [collections, query, searching]);

  // Selecting a row also expands it so the user can drill in.
  const selectCollectionRoot = (collectionId) => {
    setSelected({ collectionId, folderId: null });
    setExpandedCollections((prev) => new Set(prev).add(String(collectionId)));
  };
  const selectFolder = (collectionId, folderId) => {
    setSelected({ collectionId, folderId });
    setExpandedFolders((prev) => new Set(prev).add(String(folderId)));
  };

  const handleCreateFolderInline = async (collectionId, parentFolderId, folderName) => {
    if (!onCreateFolder) return;
    setBusy(true);
    try {
      const newId = await onCreateFolder(collectionId, parentFolderId, folderName);
      setCreatingFolderFor(null);
      if (newId) {
        setExpandedCollections((prev) => new Set(prev).add(String(collectionId)));
        if (parentFolderId) {
          setExpandedFolders((prev) => new Set(prev).add(String(parentFolderId)));
        }
        selectFolder(collectionId, newId);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCreateCollectionInline = async (collectionName) => {
    if (!onCreateCollection) return;
    setBusy(true);
    try {
      const newCollection = await onCreateCollection(collectionName);
      setCreatingCollection(false);
      if (newCollection?.id != null) {
        selectCollectionRoot(newCollection.id);
      }
    } finally {
      setBusy(false);
    }
  };

  const startCreateFolder = (collectionId, parentFolderId) => {
    setCreatingFolderFor({ collectionId, parentFolderId: parentFolderId || null });
    setExpandedCollections((prev) => new Set(prev).add(String(collectionId)));
    if (parentFolderId) {
      setExpandedFolders((prev) => new Set(prev).add(String(parentFolderId)));
    }
  };

  // Build the human-readable destination path for the footer.
  const destinationPath = useMemo(() => {
    if (!selected) return '';
    const collection = (collections || []).find((c) => String(c.id) === String(selected.collectionId));
    if (!collection) return '';
    const parts = [collection.name || 'Collection'];
    if (selected.folderId) {
      const chain = [];
      let f = getFolderById(collection, selected.folderId);
      while (f) {
        chain.unshift(f.name || 'Folder');
        f = f.parent_id ? getFolderById(collection, f.parent_id) : null;
      }
      parts.push(...chain);
    }
    return parts.join(' > ');
  }, [selected, collections]);

  const handleConfirm = () => {
    if (!selected || busy) return;
    onSelectCollection(
      selected.collectionId,
      selected.folderId,
      showNameInput ? name.trim() : undefined
    );
  };

  if (!isOpen) return null;

  const isCreatingFolderHere = (collectionId, parentFolderId) =>
    creatingFolderFor &&
    String(creatingFolderFor.collectionId) === String(collectionId) &&
    String(creatingFolderFor.parentFolderId || '') === String(parentFolderId || '');

  // Recursively render folder rows under a given parent.
  const renderFolders = (collection, parentId, depth, visibleFolderIds) => {
    let folderEntries = getOrderedChildren(collection, parentId).filter((e) => e.type === 'folder');
    if (visibleFolderIds) {
      folderEntries = folderEntries.filter((e) => visibleFolderIds.has(String(e.id)));
    }

    return (
      <>
        {isCreatingFolderHere(collection.id, parentId) && (
          <div
            className="cpicker-inline-row"
            style={{ paddingLeft: `${(depth + 1) * ROW_INDENT + 8}px` }}
          >
            <Folder size={15} className="cpicker-icon cpicker-icon--folder" />
            <InlineCreateInput
              initialValue={t('collectionSelection.newFolderName', 'New folder')}
              onSave={(value) => handleCreateFolderInline(collection.id, parentId, value)}
              onCancel={() => setCreatingFolderFor(null)}
            />
          </div>
        )}
        {folderEntries.map((entry) => {
          const folder = entry.item;
          const expanded = isFolderExpanded(folder.id);
          const isSelected =
            selected &&
            String(selected.collectionId) === String(collection.id) &&
            String(selected.folderId) === String(folder.id);
          return (
            <div key={folder.id}>
              <div
                className={`cpicker-row${isSelected ? ' cpicker-row--selected' : ''}`}
                style={{ paddingLeft: `${depth * ROW_INDENT + 8}px` }}
                onClick={() => selectFolder(collection.id, folder.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectFolder(collection.id, folder.id);
                  }
                }}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFolder(folder.id);
                  }}
                  className={`cpicker-caret${expanded ? ' cpicker-caret--open' : ''}`}
                  aria-label={expanded ? 'Collapse' : 'Expand'}
                >
                  <ChevronRight size={14} />
                </button>
                {expanded ? (
                  <FolderOpen size={15} className="cpicker-icon cpicker-icon--folder" />
                ) : (
                  <Folder size={15} className="cpicker-icon cpicker-icon--folder" />
                )}
                <span className="cpicker-row-name">{folder.name}</span>
                {allowCreate && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      startCreateFolder(collection.id, folder.id);
                    }}
                    className="cpicker-add-btn"
                    title={t('collectionSelection.newFolder', 'New folder')}
                    aria-label={t('collectionSelection.newFolder', 'New folder')}
                  >
                    <FolderPlus size={14} />
                  </button>
                )}
              </div>
              {expanded && renderFolders(collection, folder.id, depth + 1, visibleFolderIds)}
            </div>
          );
        })}
      </>
    );
  };

  const hasCollections = (collections || []).length > 0;
  const anyVisible = visibleData.some((d) => d.visible);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title || t('collectionSelection.title', 'Save request')}
      subtitle={description || t('collectionSelection.description', 'Choose where to save this request')}
      customIcon={<Folder size={24} className="cpicker-icon--folder" />}
      size="lg"
    >
      <div className="cpicker">
        {showNameInput && (
          <div>
            <label className="cpicker-field-label">
              {t('collectionSelection.nameLabel', 'Request name')}
            </label>
            <div className="cpicker-input-box">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('collectionSelection.namePlaceholder', 'Untitled request')}
                className="cpicker-input modal-input-no-focus-outline"
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Destination */}
        <div className="cpicker-dest">
          {destinationPath ? (
            <span className="cpicker-dest-line">
              <span className="cpicker-dest-label">
                {t('collectionSelection.destination', 'Destination')}:{' '}
              </span>
              <span className="cpicker-dest-path">{destinationPath}</span>
            </span>
          ) : (
            <span>{t('collectionSelection.pickDestination', 'Select a destination')}</span>
          )}
        </div>

        {/* Search + tree share one bordered panel */}
        <div className="cpicker-panel">
          <div className="cpicker-panel-search">
            <Search size={15} className="cpicker-search-icon" aria-hidden />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('collectionSelection.searchPlaceholder', 'Search collections and folders…')}
              className="cpicker-input modal-input-no-focus-outline"
              autoComplete="off"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="cpicker-clear-btn"
                aria-label={t('common.close', 'Close')}
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Tree */}
          <div className="cpicker-tree">
          {!hasCollections && (
            <div className="cpicker-empty">
              {t('collectionSelection.empty', 'No collections yet.')}
            </div>
          )}
          {hasCollections && searching && !anyVisible && (
            <div className="cpicker-empty">
              {t('collectionSelection.noResults', 'No matching collections or folders.')}
            </div>
          )}
          {visibleData.map(({ collection, visibleFolderIds, visible }) => {
            if (!visible) return null;
            const expanded = isCollectionExpanded(collection.id);
            const isSelected =
              selected &&
              String(selected.collectionId) === String(collection.id) &&
              !selected.folderId;
            return (
              <div key={collection.id} className="cpicker-collection-block">
                <div
                  className={`cpicker-row${isSelected ? ' cpicker-row--selected' : ''}`}
                  onClick={() => selectCollectionRoot(collection.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectCollectionRoot(collection.id);
                    }
                  }}
                >
                  {allowFolders ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCollection(collection.id);
                      }}
                      className={`cpicker-caret${expanded ? ' cpicker-caret--open' : ''}`}
                      aria-label={expanded ? 'Collapse' : 'Expand'}
                    >
                      <ChevronRight size={14} />
                    </button>
                  ) : (
                    <span className="cpicker-caret-spacer" />
                  )}
                  <Database size={15} className="cpicker-icon cpicker-icon--collection" />
                  <span className="cpicker-row-name cpicker-row-name--collection">{collection.name}</span>
                  {allowCreate && allowFolders && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        startCreateFolder(collection.id, null);
                      }}
                      className="cpicker-add-btn"
                      title={t('collectionSelection.newFolder', 'New folder')}
                      aria-label={t('collectionSelection.newFolder', 'New folder')}
                    >
                      <FolderPlus size={14} />
                    </button>
                  )}
                </div>
                {allowFolders && expanded && renderFolders(collection, null, 1, visibleFolderIds)}
              </div>
            );
          })}

          {/* Inline new collection */}
          {allowCreate && creatingCollection && (
            <div className="cpicker-inline-row">
              <Database size={15} className="cpicker-icon cpicker-icon--collection" />
              <InlineCreateInput
                initialValue={t('collectionSelection.newCollectionName', 'New collection')}
                onSave={handleCreateCollectionInline}
                onCancel={() => setCreatingCollection(false)}
              />
            </div>
          )}
          </div>
        </div>

        {allowCreate && !creatingCollection && (
          <button
            type="button"
            onClick={() => setCreatingCollection(true)}
            className="cpicker-new-collection"
          >
            <Plus size={15} />
            {t('collectionSelection.newCollection', 'New collection')}
          </button>
        )}

        {/* Footer */}
        <div className="cpicker-footer">
          <div className="cpicker-footer-actions">
            <button type="button" onClick={onClose} className="modal-btn-cancel">
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selected || busy}
              className="cpicker-save-btn"
            >
              {confirmLabel || t('collectionSelection.saveHere', 'Save here')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CollectionSelectionModal;
